import React, {
  useState,
  useEffect,
  lazy,
  Suspense,
  useCallback,
  useMemo,
} from "react";
import { Box, Typography } from "@mui/material";
import GettingReady from "../../components/controls/Modals/gettingReady";
import { RootState, AppDispatch } from "../../store/store";
import { resetState, setToggleSideBar } from "../../store/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import CustomTabs from "../../components/controls/CustomTabs";
import useIsMobile from "../../hooks/useIsMobile.hook";
import {
  exportCsvStream,
  formatRowsForPDF,
  formatValueForCSV,
  generateExportFileName,
  getSessionData,
  searchRecordData,
} from "../../utils/helper";
import { ToastHelper } from "../../utils/toastHelper";
import FilterList from "../../components/Filter/FilterList";
import jsPDF from "jspdf";
import "jspdf-autotable";
// import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";
import { useGenerateSampleData } from "./useGenerateSampleData.hook";
import { useExportRecordData } from "./useExportRecordsData.hook";
import {
  defaultAutoTableOptions,
  // defaultAutoTableOptions,
  // defaultJSPDFOptions,
  requiredColumns,
} from "../../utils/constants";

import { useRecordData } from "./useRecordData.hook";
import RecordNavbar from "../../components/RecordNavbar";
import { setRefresh, setSelectedRecord } from "../../store/slices/recordSlice";
import { tabsConfig } from "./tabConfig";
import { Row } from "../../interface";
//import autoTable from "jspdf-autotable";

const ExportRecord = lazy(
  () => import("../../components/controls/Modals/exportRecords")
);
const GenerateSample = lazy(
  () => import("../../components/controls/Modals/generateSample")
);
const RecordDetails = lazy(
  () => import("../../components/controls/RecordDetails")
);

const Record: React.FC = () => {
  const toastHelper = useMemo(() => new ToastHelper(), []);
  const [open, setOpen] = useState<boolean>(true);
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { selectedRecordId, refresh } = useSelector(
    (state: RootState) => state.record
  );
  const { selectedFilter } = useSelector((state: RootState) => state.filter);
  const [exportOpen, setExportOpen] = useState(false);
  const userInfo = getSessionData();
  const [activeTab, setActiveTab] = useState<number>(1);
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);

  const { getSampleRecordData } = useGenerateSampleData();
  const { exportRecordData } = useExportRecordData(activeTab);

  const [limits, setLimits] = useState({
    all: [0, 100],
    nr: [0, 100], // Needs review
    nrk: [0, 100], // No Risk
  });

  const {
    recordData: allRecord,
    isError: allError,
    isLoading: allLoading,
    poll: allPoll,
    totalCount: allTotal,
    warning: allWarning,
  } = useRecordData({
    needsReview: false,
    dataLimit: limits.all,
  });

  const {
    recordData: nrRecord,
    isError: nrError,
    isLoading: nrLoading,
    poll: nrPoll,
    totalCount: nrTotal,
    warning: nrWarning,
  } = useRecordData({
    needsReview: true,
    dataLimit: limits.nr,
  });

  const {
    recordData: nrkRecord,
    isError: nrkError,
    isLoading: nrkLoading,
    poll: nrkPoll,
    totalCount: nrkTotal,
    warning: nrkWarning,
  } = useRecordData({
    noRisk: true,
    dataLimit: limits.nrk,
  });

  //Reset the limit if the filter changes
  useEffect(() => {
    setLimits({ all: [0, 100], nr: [0, 100], nrk: [0, 100] });
  }, [selectedFilter]);

  //Handle error and  zero records for all tab
  const handleRecordErrors = useCallback(
    (isLoading: boolean, isError: boolean, warning: any) => {
      if (!isLoading) {
        if (isError) {
          toastHelper.showToast({ severity: "error", content: warning });
        } else if (warning) {
          toastHelper.showToast({
            severity: "warn",
            title: "No records found",
            content: warning,
          });
        }
      }
    },
    [toastHelper]
  );

  useEffect(() => {
    return handleRecordErrors(allLoading, allError, allWarning);
  }, [allLoading, allError, allWarning, handleRecordErrors]);
  useEffect(
    () => handleRecordErrors(nrLoading, nrError, nrWarning),
    [nrLoading, nrError, nrWarning, handleRecordErrors]
  );
  useEffect(
    () => handleRecordErrors(nrkLoading, nrkError, nrkWarning),
    [nrkLoading, nrkError, nrkWarning, handleRecordErrors]
  );

  const handleFilterSubmit = () => {
    if (!isMobile) dispatch(setToggleSideBar(true));
    setOpen(false);
  };

  const handleGenerateSummary = () => {
    setIsOpen(true);
  };

  const [searchData, setSearchData] = useState<string>("");
  // Set active tab

  const allRecordWithSearch = useMemo(
    () => searchRecordData(allRecord, searchData),
    [allRecord, searchData]
  );

  const nrRecordWithSearch = useMemo(
    () => searchRecordData(nrRecord, searchData),
    [nrRecord, searchData]
  );

  const nrkRecordWithSearch = useMemo(
    () => searchRecordData(nrkRecord, searchData),
    [nrkRecord, searchData]
  );

  const convertDataGridToCSV = async (fileName: string) => {
    const data = await exportRecordData();
    await exportCsvStream<Row>(fileName, requiredColumns, data, formatValueForCSV);
  };

  const downloadCSV = async () => {
    if(Number(currentTabTotal)  > 300000) {
       toastHelper.showToast({
        severity: "warn",
        title: "Export limit reached",
        content: `Your record selection ${currentTabTotal} rows exceeds the export limit of 300,000 rows. Refine your filters and try again.`,
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });
      return;
    }
    try {
      toastHelper.showToast({
        severity: "info",
        title: "Processing",
        content: "Export in progress...",
        options: {
          position: "top-center",
          autoClose: 500,
        }
      });
      setDownloadInProgress(true);
      const fileName = generateExportFileName("csv");
      await convertDataGridToCSV(fileName);
      setDownloadInProgress(false);
      toastHelper.showToast({
        severity: "success",
        content: `Export successful: ${fileName}`,
        options: {
            position: "top-center",
            autoClose: 500,
        }
      });

    } catch (error) {
      console.error(error);
      setDownloadInProgress(false);
      toastHelper.showToast({
        severity: "error",
        content: "Export failed due to server error. Please try again.",
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });

    }
  };

  const saveBlob = (blob: Blob, suggestedName: string)=> {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = suggestedName;
      a.click();
      URL.revokeObjectURL(a.href);
  }

    const currentTabTotal = useMemo(() => {
    switch (activeTab) {
      case 0:
        return nrTotal ;
      case 1:
        return allTotal;
      case 2:
        return nrkTotal ;
      default:
        return 0;
    }
  }, [activeTab, nrTotal, allTotal, nrkTotal]);

  const downloadPDF = async (selectedHeaders: string[]) => {
    if(Number(currentTabTotal)  > 100000) {
       toastHelper.showToast({
        severity: "warn",
        title: "Export limit reached",
        content: `Your record selection ${currentTabTotal} rows exceeds the export limit of 100,000 rows. Refine your filters and try again.`,
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });
      return;
    }
    try {
      setDownloadInProgress(true);

      toastHelper.showToast({
        severity: "info",
        title: "Processing",
        content: "Export in progress...",
        options: {
          position: "top-center",
          autoClose: 500,
        }
      });
      const doc = new jsPDF({ compress: true, unit: "pt", format: "a4" });
      const data = await exportRecordData();
      let tableRows = formatRowsForPDF(data, selectedHeaders);
      const maxWidth = 400;

      tableRows = tableRows.map((row) =>
        row.map((cell) => {
          if (typeof cell === "string") {
            const lines = doc.splitTextToSize(cell, maxWidth);
            return lines.join("\n");
          }
          return cell;
        })
      );

      (doc as any).autoTable({
        ...defaultAutoTableOptions,
        head: [selectedHeaders],
        body: tableRows,
        styles: {
          overflow: "linebreak",
        },
      });
      const fileName = generateExportFileName("pdf");

      const blob = doc.output("blob");
      await saveBlob(blob, fileName); 
      await new Promise(r => setTimeout(r, 0));
      setDownloadInProgress(false);

      toastHelper.showToast({
          severity: "success",
          content: `Export successful: ${fileName}`,
          options: {
            position: "top-center",
            autoClose: 500,
          }
      });
    } catch (error) {
      console.error(error);
      setDownloadInProgress(false);
      toastHelper.showToast({
        severity: "error",
        content: "Export failed due to server error. Please try again.",
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });
    }
  };

  const addToNeedReview = async (recordSampleState: any) => {
    const userData = getSessionData();
    const dynamicToast = new ToastHelper(true, "Please wait...");
    dynamicToast.updateToast({
      severity: "info",
      content: "Sample generation in progress...",
      title: "Processing",
      showClose: false,
      options: {
        isLoading: true,
        position: "top-center",
        autoClose: 3000,
      },
    });
    try {
      await getSampleRecordData(recordSampleState, userData);

      setActiveTab(0);
      dynamicToast.updateToast({
        severity: "success",
        content: `Sample ${recordSampleState.sampleId} generated successfully`,
        options: {
          onClose: () => dispatch(setRefresh(true)),
        },
      });
      if (nrPoll) nrPoll();
    } catch (error) {
      console.error(error);
      dynamicToast.updateToast({
        severity: "error",
        content:
          "Sample generation failed due to server error. Please try again.",
      });
    }
  };

  const handleExportClick = () => {
    setExportOpen(true);
  };

  useEffect(() => {
    if (refresh) {
      if (allPoll) allPoll();
      if (nrPoll) nrPoll();
      if (nrkPoll) nrkPoll();
    }
  }, [refresh, allPoll, nrPoll, nrkPoll]);

  const handleComponentUnmount = useCallback(() => {
    setOpen(true);
    dispatch(setSelectedRecord(""));
    dispatch(resetState());
  }, [dispatch]);

  useEffect(() => {
    return () => handleComponentUnmount();
  }, [handleComponentUnmount]);

  const tabConfig = useMemo(
    () =>
      tabsConfig(
        limits,
        nrLoading,
        nrTotal,
        allLoading,
        allTotal,
        nrkLoading,
        nrkTotal,
        nrRecordWithSearch,
        allRecordWithSearch,
        nrkRecordWithSearch,
        setLimits
      ),
    [
      limits,
      nrLoading,
      nrTotal,
      allLoading,
      allTotal,
      nrkLoading,
      nrkTotal,
      nrRecordWithSearch,
      allRecordWithSearch,
      nrkRecordWithSearch,
    ]
  );

  //To Determine disable state in action button (Generate/ Export)
  const isExportDisabled = useMemo(() => {
    switch (activeTab) {
      case 0:
        return nrTotal === 0;
      case 1:
        return allTotal === 0;
      case 2:
        return nrkTotal === 0;
      default:
        return true;
    }
  }, [activeTab, nrTotal, allTotal, nrkTotal]);
  const isGenerateDisabled = useMemo(() => {
    if (activeTab === 2) {
      return nrkTotal === 0;
    }
    return true;
  }, [activeTab, nrkTotal]);

  //Filter logic to select active tab( with filter and search)
  useEffect(() => {
    if (
      (selectedFilter?.selectedRisk.length === 1 &&
        selectedFilter?.selectedRisk.includes("NR")) ||
      (selectedFilter?.selectedStatus.length === 1 &&
        selectedFilter?.selectedStatus.includes("Available for Review")) ||
      (searchData && nrkRecordWithSearch?.length > 0)
    ) {
      setActiveTab(2);
    }

    if (
      (selectedFilter?.selectedStatus.length === 1 &&
        selectedFilter?.selectedStatus.includes("Pending Review")) ||
      (searchData && nrRecordWithSearch?.length > 0)
    ) {
      setActiveTab(0);
    }

    if (
      selectedFilter?.selectedEvaluation.length === 2 ||
      selectedFilter?.selectedRisk.length === 5 ||
      selectedFilter?.selectedStatus.length === 3 ||
      (selectedFilter?.selectedStatus.length === 1 &&
        selectedFilter?.selectedStatus.includes("Reviewed")) ||
      (searchData && allRecordWithSearch?.length > 0)
    ) {
      setActiveTab(1);
    }
  }, [
    selectedFilter.selectedRisk,
    selectedFilter.selectedStatus,
    selectedFilter.selectedEvaluation,
    allRecordWithSearch,
    nrRecordWithSearch,
    nrkRecordWithSearch,
    searchData,
  ]);

  return (
    <>
      <div className="recordContainer">
        <Typography variant="h4" className="title" data-testid="record-title">
          Text Sentinel
        </Typography>

        <Box className="recordFilter">
          <FilterList />
          <RecordNavbar
            searchData={searchData}
            disabled={{
              export: isExportDisabled || userInfo?.role?.includes("read-only") || downloadInProgress,
              generate:
                isGenerateDisabled || userInfo?.role?.includes("read-only"),
            }}
            handleExportClick={handleExportClick}
            handleGenerateSummary={handleGenerateSummary}
            onSearchChange={setSearchData}
          />
          <Box className="tabHolder">
            <CustomTabs
              activeTab={activeTab}
              name="records"
              tabConfig={tabConfig}
              onTabChange={setActiveTab}
            />
          </Box>
        </Box>
        <Suspense fallback={<>Loading...</>}>
          <ExportRecord
            open={exportOpen}
            closeModal={() => setExportOpen(false)}
            downloadCSV={downloadCSV}
            downloadPDF={downloadPDF}
            requiredColumns={requiredColumns}
          />
          <GenerateSample
            open={isOpen}
            closeModal={() => setIsOpen(false)}
            filteredRecordCount={nrkTotal}
            addToNeedReview={addToNeedReview}
          />
        </Suspense>
        <Suspense fallback={<>Loading...</>}>
          {selectedRecordId !== null && selectedRecordId !== "" && (
            <RecordDetails />
          )}
        </Suspense>
      </div>
      <GettingReady
        open={open}
        handleFilterSubmit={handleFilterSubmit}
        refresh={refresh}
      />
    </>
  );
};

export default Record;
